import { RainbowColor } from '@getaccept/fried-tofu';
import { DateTime } from 'luxon';
import { GroupFilter } from '@getaccept/lib-shared/src/enums/group-filter';
import { DsrDirectoryConstants } from '@getaccept/lib-shared/src/library/constants/dsr-directory.constants';
import { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import type { OptionMenuItem } from '@getaccept/lib-shared/src/options-menu/types/option-menu-item';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { DsrResource } from '../types/dsr-resource';
import type { DsrDirectory } from '../types/dsr-directory';
import { ResourceOption } from '../types/enums/resource-option';
import { DirectoryOption } from '../types/enums/directory-option';
import { CreateContentOption } from '../types/enums/create-content-option';
import { ResourceAccessRule } from '../types/enums/resource-access-rule';
import { DsrResourceType } from '../types/enums/dsr-resource-type';

export class LibraryHelper {
  static getTruncatedText(text: string, length: number) {
    return text?.length > length ? `${text.slice(0, length)}...` : text;
  }

  static convertToISODate(date: number) {
    return date ? DateTime.fromMillis(date).toISODate() : '';
  }

  static getResourceTypeText(resource: DsrResource) {
    switch (resource.resourceType) {
      case DsrResourceType.Directory:
        return t('folder');
      case DsrResourceType.DsrTemplate:
        return t('dsr_content_library_room_template');
      case DsrResourceType.Editor:
        return t('resource');
      default:
        return '';
    }
  }

  static getResourceBadgeIcon(resource: DsrResource) {
    switch (resource.resourceType) {
      case DsrResourceType.DsrTemplate:
        return 'template';
      case DsrResourceType.Editor:
        return 'editor-block';
      default:
        return undefined;
    }
  }

  static getResourceAccessText(resource: DsrResource) {
    switch (resource.access.readAccess) {
      case ResourceAccessRule.Private:
        return t('private');
      case ResourceAccessRule.Shared:
        return t('public');
      default:
        return '';
    }
  }

  static getResourceTypeIcon(resource: DsrResource) {
    switch (resource.resourceType) {
      case DsrResourceType.Directory:
        return 'folder-open';
      case DsrResourceType.DsrTemplate:
        return 'document-dsr';
      case DsrResourceType.Editor:
        return 'editor-block';
      default:
        return undefined;
    }
  }

  static getResourceBadgeColor(resource: DsrResource) {
    switch (resource.resourceType) {
      case DsrResourceType.Directory:
        return RainbowColor.Emerald;
      case DsrResourceType.DsrTemplate:
        return RainbowColor.Purple;
      case DsrResourceType.Editor:
      default:
        return RainbowColor.Ocean;
    }
  }

  static getAccessBadgeColor(resource: DsrResource) {
    switch (resource.access.readAccess) {
      case ResourceAccessRule.Private:
        return RainbowColor.Leather;
      case ResourceAccessRule.Shared:
        return RainbowColor.Forest;
      default:
        return RainbowColor.Leather;
    }
  }

  static getIsUpdated(item: DsrResource | DsrDirectory) {
    return item.createdAt !== item.updatedAt;
  }

  static getBreadcrumbsFromPath(path: string) {
    return path.slice(0, -1).split('/');
  }

  static getPathFromBreadcrumbs(breadcrumbs: string[], index: number) {
    const isRoot = index === 0;
    return isRoot ? '/' : `/${breadcrumbs.slice(1, index + 1).join('/')}/`;
  }

  static getActiveDirectoryTitleFromPath(path: string) {
    if (path === DsrDirectoryConstants.RootPath) {
      return t('library');
    }
    return LibraryHelper.getBreadcrumbsFromPath(path).slice(-1)[0];
  }

  static getResourceOptions = (
    resource: DsrResource,
    isResourceEditable: boolean,
    hasTagFlag: boolean,
    hasTags: boolean
  ): OptionMenuItem[] => {
    const isTemplate = resource.resourceType === DsrResourceType.DsrTemplate;
    const isResourcePublic = resource.access.readAccess === ResourceAccessRule.Shared;
    const options: OptionMenuItem[] = [
      {
        title: isTemplate ? t('dsr_open_template') : t('dsr_open_resource'),
        icon: isTemplate ? 'template' : 'editor-block',
        callback: ResourceOption.Edit,
        show: true,
      },
      {
        title: t('dsr_rename_option'),
        icon: 'edit',
        callback: ResourceOption.Rename,
        show: isResourceEditable,
      },
      {
        title: hasTags ? t('dr_edit_tags_title') : t('dr_add_tags_drop'),
        icon: 'tags',
        callback: ResourceOption.EditTags,
        show: isResourceEditable && hasTagFlag,
      },
      {
        title: t('duplicate'),
        icon: 'duplicate',
        callback: ResourceOption.Duplicate,
        show: true,
      },
      {
        title: t('move-to-folder'),
        icon: 'folder-open',
        callback: ResourceOption.Move,
        show: isResourceEditable,
      },
      {
        title: isResourcePublic ? t('dr_change_to_private') : t('dr_change_to_public'),
        icon: 'replace-item',
        callback: ResourceOption.ChangeAccess,
        disabled: isResourcePublic && !!resource.linkedTemplatesCount,
        tooltip:
          isResourcePublic && !!resource.linkedTemplatesCount
            ? t('dr_linked_change_access_disabled_tooltip')
            : '',
        show: isResourceEditable,
      },
      {
        title: isTemplate ? t('template_delete_button') : t('dsr_delete_resource'),
        icon: 'delete-bin',
        callback: ResourceOption.Delete,
        disabled: !!resource.linkedTemplatesCount,
        tooltip: resource.linkedTemplatesCount ? t('dr_linked_delete_disabled_tooltip') : '',
        show: isResourceEditable,
      },
    ];

    if (isTemplate) {
      const editOptionIndex = options.findIndex(({ callback }) => callback === ResourceOption.Edit);
      const createRoomOption = {
        title: t('dsr_content_lib__create_room'),
        icon: 'document-dsr',
        callback: ResourceOption.CreateRoom,
        show: true,
      } as OptionMenuItem;
      options.splice(editOptionIndex + 1, 0, createRoomOption);
    }

    return options;
  };

  static getCreateContentOptions = (): OptionMenuItem[] => [
    {
      title: t('dsr_content_library_room_template'),
      icon: 'document-dsr',
      callback: CreateContentOption.RoomTemplate,
    },
    {
      title: t('resource'),
      icon: 'editor-block',
      callback: CreateContentOption.Resource,
      divider: true,
    },
    {
      title: t('folder'),
      icon: 'folder-open',
      callback: CreateContentOption.Directory,
    },
  ];

  static getDirectoryOptions = (): OptionMenuItem[] => [
    { title: t('dsr_edit_folder'), icon: 'edit', callback: DirectoryOption.Edit, show: true },
    {
      title: t('dsr_delete_folder'),
      icon: 'delete-bin',
      callback: DirectoryOption.Delete,
      show: true,
    },
  ];

  static getDirectoryTitleValidationError = (
    title: string,
    subDirectories: DsrDirectory[],
    oldTitle?: string
  ) => {
    let titleValidationError = '';

    if (oldTitle) {
      const titleUpdated = title !== oldTitle;
      if (!titleUpdated) {
        return t('name-not-changed');
      }
    }

    if (!title) {
      return t('name-is-required');
    }

    const isDuplicateTitle = subDirectories.some(
      ({ title: subDirectoryTitle }) => subDirectoryTitle === title
    );

    if (isDuplicateTitle) {
      return t('folder-name-exists');
    }

    const hasInvalidCharacters = /\/|{|}|\\/g.test(title);
    if (hasInvalidCharacters) {
      titleValidationError = t('invalid-folder-name').toString();
    }

    return titleValidationError;
  };

  static removeDuplicateDirectories(array: DsrDirectory[]): DsrDirectory[] {
    return Array.from(new Set(array));
  }

  static isResourceAccessible(resource: DsrResource, userRole: UserRoleLegacy, userId: string) {
    return (
      resource.user?.id === userId ||
      userRole === UserRoleLegacy.Admin ||
      resource.access.readAccess === ResourceAccessRule.Shared
    );
  }

  static isResourceEditable(resource: DsrResource, userRole: UserRoleLegacy, userId: string) {
    return resource.user?.id === userId || userRole === UserRoleLegacy.Admin;
  }

  static getFilterTabs = (activeFilter: GroupFilter) => [
    {
      label: t('all_content'),
      key: GroupFilter.All,
      isActive: activeFilter === GroupFilter.All,
      dataExternal: 'library-filter-tabs-all',
    },
    {
      label: t('created_by_me'),
      key: GroupFilter.My,
      isActive: activeFilter === GroupFilter.My,
      dataExternal: 'library-filter-tabs-my',
    },
  ];
}
